import { router } from "umi";
import { removeCampaignsQueries } from "@/utils/campaigns";
import { clearImpersonationMetadata, getImpersonatorAdminUserEmail, setImpersonationMetadata, } from "@/utils/impersonation";
import request from "@/utils/request";
import { getRouteByPath, isRedirectSafe } from "@/utils/route";
import { stopTrackingLoggedInUserForDatadog } from "@/utils/setupDatadog";
import { setSessionStorage, removeSessionStorage, getLocalStorage, setLocalStorage, removeLocalStorage } from "@/utils/storage";
import { removeTermsAndConditionsQueries } from "@/utils/termsAndConditions";
import { cleanObj, getUrlParams } from "@/utils/utils";
import { getProfileData } from "@/services/account";
import { removeTermsDocumentsQueries } from "@/services/termsDocuments";
import { getAppConfigData } from "@/services/user";
import { PHONE_VERIFICATION_REQUIRED_KEY } from "@/pages/user/phone-verification";
function setLoginData(resp) {
    let data;
    let response;
    if (resp.response) {
        data = resp.data;
        response = resp.response;
    }
    if (response?.ok) {
        setLocalStorage("dataAuth", data);
    }
    if (response) {
        return response;
    }
    return resp;
}
const redirectUrlKey = (username) => `redirectUrl-${username}`;
const consumeStoredRedirectPath = (username) => {
    const key = redirectUrlKey(username);
    const stored = getLocalStorage(key);
    removeLocalStorage(key);
    // Clear leftover sessionStorage keys from before the localStorage migration
    removeSessionStorage(key);
    return typeof stored === "string" ? stored : null;
};
const redirectFn = (username, configPerm) => {
    if (!username) {
        router.replace("");
        return;
    }
    let redirectUrl = getUrlParams("returnUrl");
    if (!redirectUrl) {
        const search = decodeURIComponent(window.location.search);
        const params = new URLSearchParams(search).toString();
        if (params.startsWith("?returnUrl=") || params.startsWith("returnUrl=")) {
            redirectUrl = decodeURIComponent(params).split("returnUrl=")[1] || "";
        }
    }
    // Query returnUrl wins; still drop any stored path so it cannot leak into a later login
    if (redirectUrl) {
        consumeStoredRedirectPath(username);
        redirectUrl = decodeURIComponent(redirectUrl);
    }
    else {
        const returnPath = consumeStoredRedirectPath(username);
        // Keep returnPath percent-encoded so ?/& in the saved path stay inside returnUrl
        redirectUrl = returnPath ? `/?returnUrl=${encodeURIComponent(returnPath)}` : "/";
    }
    // RedirectLayout handles /?returnUrl=... (permission check). Direct paths come from login ?returnUrl=.
    const isSafe = isRedirectSafe(redirectUrl, configPerm);
    if (isSafe) {
        router.replace(redirectUrl);
        return;
    }
    router.replace("/");
};
export async function login({ username, password, code, provider, recoveryCode }) {
    const data = cleanObj(recoveryCode
        ? {
            grant_type: "password",
            client_id: REACT_APP_CLIENT_ID,
            scope: REACT_APP_SCOPE,
            username,
            password,
            RecoveryCode: recoveryCode,
        }
        : {
            grant_type: "password",
            client_id: REACT_APP_CLIENT_ID,
            scope: REACT_APP_SCOPE,
            username,
            password,
            TwoFactorCode: code,
            TwoFactorProvider: provider,
        });
    const loginResponse = await request(`${REACT_APP_IDENTITY_SERVER_BASE_URL}/connect/token`, {
        method: "POST",
        body: new URLSearchParams(Object.entries(data)).toString(),
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
    });
    // Check if two-factor authentication is required
    if (loginResponse?.error === "RequiresTwoFactor") {
        return {
            requiresTwoFactor: true,
            twoFactorToken: loginResponse.data.twoFactorToken,
            userId: loginResponse.data.userId,
            error: true,
        };
    }
    // store login data
    setLoginData(loginResponse);
    // if response is ok, redirect the user
    if (loginResponse?.response?.ok) {
        let configPerm = {};
        // Fetch app configuration to check phone verification status
        try {
            const appConfig = await getAppConfigData();
            const profile = await getProfileData();
            configPerm = appConfig?.auth.grantedPolicies;
            const phoneNumberVerified = profile?.phoneNumberConfirmed ?? false;
            const aRoleRequires2FA = (profile?.roles || [])?.some((role) => role.requires2FA) ?? false;
            const twoFactorSetting = appConfig?.features?.values?.["Identity.TwoFactor"];
            const isSystem2FAActive = twoFactorSetting === "Optional" || twoFactorSetting === "Forced";
            if (!phoneNumberVerified) {
                if (isSystem2FAActive && !aRoleRequires2FA) {
                    // only set the key if the 2FA setting is forced and is not a role that requires 2FA
                    const mfaRequired = twoFactorSetting === "Forced";
                    setSessionStorage(PHONE_VERIFICATION_REQUIRED_KEY, mfaRequired);
                }
                if (isSystem2FAActive || aRoleRequires2FA) {
                    // if the 2FA setting is optional or forced, or if a role that requires 2FA, redirect to the phone verification page
                    let queryParams = window.location.search;
                    if (queryParams.startsWith("?")) {
                        queryParams = queryParams.slice(1);
                    }
                    router.replace(`/user/phone-verification${queryParams ? `?${queryParams}` : ""}`);
                    return { ok: true };
                }
            }
        }
        catch (error) {
            // If app config or phone verification check fails, continue to normal redirect
            // eslint-disable-next-line no-console
            console.warn("Failed to check phone verification requirements:", error);
        }
        redirectFn(username, configPerm);
        return { ok: true };
    }
    return { error: loginResponse.error };
}
const updateTokensForUserImpersonation = async (userIdToImpersonate, returnUserName, getNewConfigPerm, onSuccess) => {
    const authData = getLocalStorage("dataAuth");
    const data = {
        client_id: `${REACT_APP_CLIENT_ID}`,
        grant_type: "Impersonation",
        scope: REACT_APP_SCOPE,
        access_token: authData.access_token,
        UserId: userIdToImpersonate ?? null,
    };
    const impersonateResponse = await request(`${REACT_APP_IDENTITY_SERVER_BASE_URL}/connect/token`, {
        method: "POST",
        data: new URLSearchParams(Object.entries(data)).toString(),
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
    });
    if (impersonateResponse?.response?.ok) {
        setLoginData(impersonateResponse);
        // Persist/clear impersonation metadata only after the token swap succeeds so a failed
        // start/end cannot leave localStorage out of sync with the actual session.
        onSuccess?.();
        await getNewConfigPerm();
        // Terms docs are role-scoped and cached forever; clear on identity switch so callers refetch.
        removeTermsDocumentsQueries();
        // Clear acceptance/SMS consent cache so we never prompt (or accept) as the wrong user.
        removeTermsAndConditionsQueries();
        redirectFn(returnUserName);
        return true;
    }
    return false;
};
export async function startAccountImpersonation({ userIdToImpersonate, returnEmail, configPerm, getNewConfigPerm, }) {
    const { pathname, search } = window.location;
    const route = getRouteByPath(pathname);
    const shouldSaveRedirect = !!(route && !route.cannotRedirectTo);
    const returnPath = pathname + search;
    await updateTokensForUserImpersonation(userIdToImpersonate, null, getNewConfigPerm, () => {
        setImpersonationMetadata(returnEmail, configPerm);
        // Only persist return path after a successful token swap (localStorage survives failures/restarts)
        if (shouldSaveRedirect) {
            setLocalStorage(redirectUrlKey(returnEmail), returnPath);
        }
    });
}
export async function endAccountImpersonation({ getNewConfigPerm }) {
    const adminUserEmail = getImpersonatorAdminUserEmail();
    await updateTokensForUserImpersonation(null, adminUserEmail, getNewConfigPerm, () => {
        clearImpersonationMetadata();
    });
}
export async function revokeToken({ token }) {
    const data = {
        client_id: REACT_APP_CLIENT_ID,
        token,
    };
    const revokeTokenRequest = await request(`${REACT_APP_IDENTITY_SERVER_BASE_URL}/connect/revocat`, {
        method: "POST",
        data: new URLSearchParams(Object.entries(data)).toString(),
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
    });
    removeLocalStorage("dataAuth");
    return revokeTokenRequest;
}
export async function logout(username) {
    try {
        const { pathname, search } = window.location;
        const dataAuth = getLocalStorage("dataAuth");
        const token = dataAuth?.refresh_token;
        const route = getRouteByPath(pathname);
        if (username && route !== null && !route.cannotRedirectTo) {
            // save current page so login can restore it (localStorage survives new tabs)
            setLocalStorage(redirectUrlKey(username), pathname + search);
        }
        if (token) {
            await revokeToken({ token });
        }
        // localStorage survives browser restarts; don't leave impersonation markers after logout
        clearImpersonationMetadata();
        stopTrackingLoggedInUserForDatadog();
        removeCampaignsQueries();
        removeTermsAndConditionsQueries();
        return { ok: true };
    }
    catch (error) {
        console.error(error);
        return { error };
    }
}
export async function refreshToken() {
    const dataAuthGet = getLocalStorage("dataAuth");
    const refreshTokenStr = dataAuthGet?.refresh_token;
    const data = {
        client_id: `${REACT_APP_CLIENT_ID}`,
        grant_type: "refresh_token",
        scope: REACT_APP_SCOPE,
        refresh_token: refreshTokenStr,
    };
    const refreshTokenRequest = await request(`${REACT_APP_IDENTITY_SERVER_BASE_URL}/connect/token`, {
        method: "POST",
        data: new URLSearchParams(Object.entries(data)).toString(),
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
    });
    return setLoginData(refreshTokenRequest);
}
